/**
 * Hook for fetching and caching admin enum metadata
 *
 * Fetches all admin-relevant enums from the backend on mount,
 * caches them in state, and provides helper functions for
 * looking up enum values and building select options.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAdminApi } from "@/services/api/admin-api";
import type { EnumMetadataDto } from "@/types/api/admin";

export interface UseAdminEnumsResult {
    /** All loaded enum metadata */
    enums: EnumMetadataDto[];
    /** Whether enum data is currently loading */
    isLoading: boolean;
    /** Whether enum data has been loaded at least once */
    isLoaded: boolean;
    /** Get a specific enum by name */
    getEnum: (name: string) => EnumMetadataDto | undefined;
    /** Get select options ({ label, value }) for a specific enum */
    getOptions: (name: string) => { label: string; value: string }[];
}

/**
 * Fetches admin enum metadata on mount and provides lookup helpers.
 */
export function useAdminEnums(): UseAdminEnumsResult {
    const adminApi = useAdminApi();
    const [enums, setEnums] = useState<EnumMetadataDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function fetchEnums() {
            setIsLoading(true);
            try {
                const response = await adminApi.getEnumMetadata();
                if (!cancelled) {
                    setEnums(response.data);
                    setIsLoaded(true);
                }
            } catch (error) {
                console.error("Failed to fetch admin enums:", error);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        fetchEnums();

        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    const enumMap = useMemo(() => {
        const map = new Map<string, EnumMetadataDto>();
        for (const e of enums) {
            map.set(e.name, e);
        }
        return map;
    }, [enums]);

    const getEnum = useCallback(
        (name: string): EnumMetadataDto | undefined => {
            return enumMap.get(name);
        },
        [enumMap],
    );

    const getOptions = useCallback(
        (name: string): { label: string; value: string }[] => {
            const enumMeta = enumMap.get(name);
            if (!enumMeta) return [];
            return enumMeta.values.map((v) => ({
                label: v.name,
                value: String(v.value),
            }));
        },
        [enumMap],
    );

    return { enums, isLoading, isLoaded, getEnum, getOptions };
}
